import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { translate } from '@jsverse/transloco';
import { NgbPopover, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { DropdownObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { FilterDateCalendarComponent } from 'app/components/filter-date-calendar/filter-date-calendar.component';

import { NumericMetadataPopoverComponent } from 'app/components/numeric-metadata-popover/numeric-metadata-popover.component';
import {
  Filter,
  FilterService,
  FilterSet,
  OperatorType,
} from 'app/modules/collection/services/filter.service';

import { Utils } from 'app/utils/utils';

@Component({
  selector: 'app-lex-xi-filter-pill',
  imports: [
    FilterDateCalendarComponent,
    NumericMetadataPopoverComponent,
    AppSharedModule,
    NgbPopoverModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './lex-xi-filter-pill.component.html',
  styleUrls: ['./lex-xi-filter-pill.component.scss'],
})
export class LexXiFilterPillComponent implements OnChanges, OnInit {
  @Input() filter: Filter;
  @Input() userSearch = false;
  @Input() dataSource: string;
  @Input() isParentFilter = false;
  @Input() isActionDisabled: boolean;
  @Input() isPillDisabled = false;
  @Input() actionTooltip?: string;
  @Input() filterOperator: string;
  @Input() selectedFilters: FilterSet;
  @Input() sentimentScale?: number;
  @Input() sectionFiltering = false;
  @Output() filterAction = new EventEmitter<{
    filterMoveOrDelete: OperatorType | 'remove' | 'document' | 'section';
    newFilter: Filter;
  }>();
  @ViewChild('dateMetadataCalendar', { read: ElementRef }) dateMetadataCalendar: ElementRef;
  @ViewChild('numericMetadataPopover') numericMetadataPopover: NgbPopover;

  pillText = '';
  pillTextExtra = '';
  shortLabel = '';
  pillTooltip: string;
  dropdown: DropdownObject[] = [];
  selectedItems = [];
  private filterType: string;

  constructor(private filterService: FilterService) {}

  ngOnInit() {
    this.buildDropdown();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.sentimentScale && changes.sentimentScale.currentValue) {
      this.updateFilterSentimentScale();
    }

    this.selectedItems = [];
    this.setPillText();
    this.setPillTooltip();
    this.setFilterType();
    this.selectedItems.push(this.getSelectedItemForDropdown());

    if (changes.sectionFiltering && !changes.sectionFiltering.firstChange) {
      this.buildDropdown();
    }
  }

  private setPillText() {
    const pillTexts = this.filterService.getPillTexts(this.filter, 30);

    this.pillText = pillTexts.text;
    this.pillTextExtra = pillTexts.textExtra;
    this.shortLabel = pillTexts.shortLabel;
  }

  private setPillTooltip() {
    const dataField = this.filter.displayTitle
      ? this.filter.displayTitle
      : this.filterService.translateTitle(this.filter.analyticItem);

    let dataFieldSubLabel = '';
    if (!this.filter.analyticItem.includes('taxonomy') && this.filter.subLabel) {
      dataFieldSubLabel = ` (${this.filter.subLabel})`;
    }

    const pillTooltip = translate('lex-xi-filter-pill-component.pill-tooltip', {
      source: this.dataSource || 'N/A',
      field: dataField,
      subField: dataFieldSubLabel,
    });
    this.pillTooltip = pillTooltip.concat(Utils.unescapeHtml(this.filter.displayValue));

    if (this.filter.analyticItem.includes('taxonomy')) {
      this.pillTooltip += `\n${translate('lex-xi-filter-pill-component.pill-tooltip-path', {
        taxonomyPath: this.pillTextExtra,
      })}`;
    }
  }

  private setFilterType() {
    this.filterType = this.filter.analyticItem;
    if (this.filterType.includes('sentimentPolarity')) {
      if (this.sentimentScale) {
        this.filter.analyticItem = this.filter.analyticItem.slice(0, -1) + this.sentimentScale;
        this.setDocumentSentimentOptions('sentimentPolarity' + this.sentimentScale);
      } else {
        if (this.filterType.includes('sentimentPolarity3')) {
          this.setDocumentSentimentOptions('sentimentPolarity3');
        } else if (this.filterType.includes('sentimentPolarity5')) {
          this.setDocumentSentimentOptions('sentimentPolarity5');
        }
      }
    } else if (this.filterType.includes('createdAt')) {
      this.setDateMetadataOptions();
    } else if (this.filterType.includes('metadata') && this.filter.value.includes('numericrange')) {
      this.setNumericMetadataOptions();
    }
  }

  //Set dropdown options and disable relevant options
  private setDocumentSentimentOptions(sentimentScale: string) {
    let sentimentScaleDropdown: DropdownObject;

    if (sentimentScale === 'sentimentPolarity3') {
      sentimentScaleDropdown = {
        title: translate('lex-xi-filter-pill-component.edit'),
        id: 'edit',

        submenu: [
          {
            title: translate('lex-xi-filter-pill-component.positive'),
            id: 'positive',
            disabled: false,
          },
          {
            title: translate('lex-xi-filter-pill-component.neutral'),
            id: 'neutral',
            disabled: false,
          },
          {
            title: translate('lex-xi-filter-pill-component.negative'),
            id: 'negative',
            disabled: false,
          },
        ],
      };
    } else if (sentimentScale === 'sentimentPolarity5') {
      sentimentScaleDropdown = {
        title: translate('lex-xi-filter-pill-component.edit'),
        id: 'edit',
        submenu: [
          {
            title: translate('lex-xi-filter-pill-component.very-positive'),
            id: 'very positive',
            disabled: false,
          },
          {
            title: translate('lex-xi-filter-pill-component.positive'),
            id: 'somewhat positive',
            disabled: false,
          },
          {
            title: translate('lex-xi-filter-pill-component.neutral'),
            id: 'neutral',
            disabled: false,
          },
          {
            title: translate('lex-xi-filter-pill-component.negative'),
            id: 'somewhat negative',
            disabled: false,
          },
          {
            title: translate('lex-xi-filter-pill-component.very-negative'),
            id: 'very negative',
            disabled: false,
          },
        ],
      };
    }

    for (const key in this.selectedFilters) {
      if (this.selectedFilters.hasOwnProperty(key)) {
        for (const filter of this.selectedFilters[key]) {
          if (filter.analyticItem.includes(sentimentScale)) {
            const sentimentFilter = sentimentScaleDropdown.submenu.find(
              (item) => item.id === filter.value
            );
            if (
              sentimentFilter &&
              filter.fromWidgetGroup === this.filter.fromWidgetGroup &&
              filter.fromWidgetGroupParent === this.filter.fromWidgetGroupParent
            ) {
              sentimentFilter.disabled = true;
            }
          }
        }
      }
    }
    if (this.dropdown.length === 3) {
      this.dropdown.shift();
    }
    this.dropdown.unshift(sentimentScaleDropdown);
    this.selectedItems.push(this.filter.value);
    this.dropdown = [...this.dropdown];
  }

  private setNumericMetadataOptions() {
    if (this.dropdown.length === 3) {
      this.dropdown.shift();
    }
    const numericMetadataDropdown = {
      title: translate('lex-xi-filter-pill-component.edit'),
      id: 'numericMetadataEdit',
    };
    this.dropdown.unshift(numericMetadataDropdown);
    this.dropdown = [...this.dropdown];
  }

  private setDateMetadataOptions() {
    if (this.dropdown.length === 3) {
      this.dropdown.shift();
    }
    const dateMetadataDropdown = {
      title: translate('lex-xi-filter-pill-component.edit'),
      id: 'dateMetadataEdit',
    };
    this.dropdown.unshift(dateMetadataDropdown);
    this.dropdown = [...this.dropdown];
  }

  private getIsFilterValueChange(id: string): boolean {
    return (
      id !== 'and' &&
      id !== 'not' &&
      id !== 'or' &&
      id !== 'remove' &&
      id !== 'document' &&
      id !== 'section'
    );
  }

  onPillAction(id: string) {
    //If value change action
    if (this.getIsFilterValueChange(id)) {
      this.selectedItems = [...this.selectedItems];
      if (
        this.filterType.includes('sentimentPolarity3') ||
        this.filterType.includes('sentimentPolarity5')
      ) {
        this.filter.displayName = id;
        this.filter.displayValue = id;
        this.filter.value = id;
      } else if (id === 'dateMetadataEdit') {
        setTimeout(() => {
          this.dateMetadataCalendar.nativeElement
            .querySelector('xi-calendar .kxi-calendar-popout-dropper')
            .click();
        });

        return;
      } else if (id === 'numericMetadataEdit') {
        this.numericMetadataPopover.open();

        return;
      }
      this.filterAction.emit({
        filterMoveOrDelete: null,
        newFilter: this.filter,
      });
      //If move/delete action
    } else {
      this.filterAction.emit({
        filterMoveOrDelete: id as OperatorType | 'remove' | 'document' | 'section',
        newFilter: null,
      });
    }
  }

  onPopoverChange(filter: Filter) {
    if (!filter) {
      this.numericMetadataPopover.close();
    } else {
      this.filter = filter;
      this.filterAction.emit({
        filterMoveOrDelete: null,
        newFilter: this.filter,
      });
      this.numericMetadataPopover.close();
    }
  }

  onFilterDateChange(filter: Filter) {
    this.filter.displayValue = filter.displayValue;
    this.filter.value = filter.value;
    this.filter.key = filter.key;

    this.filterAction.emit({
      filterMoveOrDelete: null,
      newFilter: this.filter,
    });
  }

  private updateFilterSentimentScale() {
    //if going from 5 to 3
    if (this.sentimentScale === 3) {
      switch (this.filter.value) {
        case 'very positive':
        case 'somewhat positive':
          this.filter.value = 'positive';
          this.filter.displayValue = translate('lex-xi-filter-pill-component.positive');
          break;
        case 'very negative':
        case 'somewhat negative':
          this.filter.value = 'negative';
          this.filter.displayValue = translate('lex-xi-filter-pill-component.negative');
          break;
      }
    }
    //if going from 3 to 5
    if (this.sentimentScale === 5) {
      switch (this.filter.value) {
        case 'positive':
          this.filter.value = 'very positive';
          this.filter.displayValue = translate('lex-xi-filter-pill-component.very-positive');
          break;
        case 'negative':
          this.filter.value = 'very negative';
          this.filter.displayValue = translate('lex-xi-filter-pill-component.very-negative');
          break;
      }
    }
  }

  private buildDropdown(): void {
    const moveAndDo: DropdownObject = {
      title: translate('lex-xi-filter-pill-component.all'),
      id: 'and',
    };

    if (this.sectionFiltering) {
      moveAndDo.submenu = [
        {
          title: translate('lex-xi-filter-pill-component.document'),
          id: 'document',
          disabled:
            this.filterOperator === 'and' &&
            !this.filter.sectionField &&
            !this.filter.selectedSections?.length,
        },
        {
          title: translate('lex-xi-filter-pill-component.section'),
          id: 'section',
          disabled:
            (this.filterOperator === 'and' &&
              (!!this.filter.sectionField || !!this.filter.selectedSections?.length)) ||
            !this.filterService.isAllowedForSection(this.filter),
        },
      ];
    }

    this.dropdown = [
      {
        title: translate('lex-xi-filter-pill-component.move'),
        id: 'move',
        submenu: [
          moveAndDo,
          { title: translate('lex-xi-filter-pill-component.any'), id: 'or' },
          { title: translate('lex-xi-filter-pill-component.none'), id: 'not' },
        ],
      },
      { title: translate('lex-xi-filter-pill-component.remove'), id: 'remove' },
    ];

    if (this.filter.parentFilter) {
      this.dropdown.splice(0, 1);
    }

    this.setFilterType();
  }

  private getSelectedItemForDropdown(): string {
    if (this.filterOperator === 'and' && this.sectionFiltering) {
      return this.filter.sectionField || this.filter.selectedSections?.length
        ? 'section'
        : 'document';
    }

    return this.filterOperator;
  }
}
